import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Clock,
  Calendar,
  Film,
  Wrench,
  Trophy,
  User,
  ArrowUpRight,
} from "lucide-react";
import { tapes as publicTapes, profile } from "../mock/mock";
import { tapeDetails, vaultTapes } from "../mock/enrich";
import { Button } from "../components/ui/button";

const allTapes = [...publicTapes, ...vaultTapes];

const TapeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const tape = useMemo(() => allTapes.find((t) => t.id === id), [id]);
  const details = tapeDetails[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!tape) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-[11px] mono uppercase tracking-widest text-[#e4572e]">
            Signal lost
          </div>
          <h1
            className="mt-4 text-5xl md:text-6xl"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Tape not found.
          </h1>
          <Button
            onClick={() => navigate("/")}
            className="mt-8 h-11 px-6 bg-[#f2ece1] text-[#0e0b09] hover:bg-[#f5b14e] rounded-full"
          >
            Rewind to the shelf
          </Button>
        </div>
      </div>
    );
  }

  const related = (details?.related || [])
    .map((rid) => allTapes.find((t) => t.id === rid))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0e0b09] text-[#f2ece1]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#0e0b09]/85 backdrop-blur-md border-b border-[#1f1a15]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[13px] text-[#f2ece1]/80 hover:text-[#f5b14e] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the shelf
          </button>
          <div className="text-[11px] mono text-[#f2ece1]/50 hidden md:block">
            CAT #{tape.id.toUpperCase()}-{tape.year} · TC 00:00:00:00
          </div>
          <Link
            to="/#contact"
            className="text-[13px] text-[#f2ece1]/80 hover:text-[#f5b14e] transition-colors"
          >
            Get in touch ↗
          </Link>
        </div>
      </div>

      {/* Hero — title + still */}
      <section className="relative pt-12 md:pt-20 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <div
              className="text-[11px] mono uppercase tracking-[0.3em]"
              style={{ color: tape.accent }}
            >
              ▶ {tape.subtitle} · {tape.year}
            </div>
            <h1
              className="mt-4 text-6xl md:text-8xl leading-[0.88]"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {tape.title}
            </h1>
            <p className="mt-6 text-lg text-[#f2ece1]/75 leading-relaxed max-w-2xl">
              {details?.story || tape.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`https://www.youtube.com/watch?v=${tape.youtubeId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#f2ece1] text-[#0e0b09] text-[13px] font-medium hover:bg-[#f5b14e] transition-colors"
              >
                <Play className="w-4 h-4 fill-[#0e0b09]" /> Watch the reel
              </a>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-[#2a221b] text-[#f2ece1] text-[13px] hover:border-[#f5b14e] hover:text-[#f5b14e] transition-colors"
              >
                Request BTS notes ↗
              </a>
            </div>
          </div>

          {/* Painted still */}
          <div className="md:col-span-5">
            <div
              className="aspect-[4/5] rounded-2xl relative overflow-hidden border border-[#2a221b]"
              style={{ background: details?.still || tape.body }}
            >
              <div className="absolute inset-0 grain scanlines" />
              <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between">
                <div>
                  <div
                    className="text-[10px] mono uppercase tracking-widest"
                    style={{ color: tape.accent }}
                  >
                    Still · Frame 01
                  </div>
                  <div
                    className="text-2xl mt-1"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    {tape.title}
                  </div>
                </div>
                <div
                  className="w-2 h-2 rounded-full rec-dot"
                  style={{ background: tape.accent }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="relative px-6 md:px-10 max-w-[1400px] mx-auto">
        <div className="aspect-video w-full rounded-2xl overflow-hidden border border-[#2a221b] relative scanlines">
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${tape.youtubeId}?rel=0&modestbranding=1&playsinline=1`}
            title={tape.title}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>

      {/* Meta grid */}
      <section className="py-16 md:py-20 max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-4 space-y-6">
          <MetaItem icon={<User className="w-3.5 h-3.5" />} label="Client" value={details?.client || "—"} />
          <MetaItem icon={<Film className="w-3.5 h-3.5" />} label="Format" value={details?.format || "—"} />
          <MetaItem icon={<Clock className="w-3.5 h-3.5" />} label="Runtime" value={tape.runtime} />
          <MetaItem icon={<Calendar className="w-3.5 h-3.5" />} label="Year" value={tape.year} />
          <MetaItem icon={<Play className="w-3.5 h-3.5" />} label="Category" value={tape.category} />
        </div>

        <div className="md:col-span-4">
          <SectionTitle>Credits</SectionTitle>
          <ul className="mt-4 space-y-3">
            <li className="flex items-baseline justify-between gap-4 border-b border-[#2a221b] pb-3">
              <span className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
                {details?.role || "Director"}
              </span>
              <span className="text-[15px]">{profile.name}</span>
            </li>
            {(details?.credits || []).map((c) => (
              <li
                key={c.role}
                className="flex items-baseline justify-between gap-4 border-b border-[#2a221b] pb-3"
              >
                <span className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
                  {c.role}
                </span>
                <span className="text-[15px]">{c.name}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 space-y-8">
          <div>
            <SectionTitle>
              <Wrench className="w-3.5 h-3.5 inline mr-2" />
              Tools
            </SectionTitle>
            <div className="mt-4 flex flex-wrap gap-2">
              {(details?.tools || []).map((t) => (
                <span
                  key={t}
                  className="text-[11px] mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#2a221b] text-[#f2ece1]/75"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {details?.festivals?.length ? (
            <div>
              <SectionTitle>
                <Trophy className="w-3.5 h-3.5 inline mr-2" />
                Selections
              </SectionTitle>
              <ul className="mt-4 space-y-2">
                {details.festivals.map((f) => (
                  <li key={f} className="text-[14px] text-[#f2ece1]/80">
                    ✦ {f}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-24 md:pb-32 max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-6">
            <SectionTitle>Related tapes</SectionTitle>
            <Link
              to="/#archive"
              className="text-[12px] mono uppercase tracking-widest text-[#f2ece1]/60 hover:text-[#f5b14e]"
            >
              See the full archive ↗
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/tape/${r.id}`}
                className="group rounded-xl border border-[#2a221b] bg-[#120e0a] p-5 hover:border-[#f5b14e]/60 transition-colors"
              >
                <div
                  className="aspect-video rounded-lg mb-4"
                  style={{
                    background: tapeDetails[r.id]?.still || r.body,
                  }}
                />
                <div
                  className="text-[11px] mono uppercase tracking-widest"
                  style={{ color: r.accent }}
                >
                  {r.subtitle}
                </div>
                <div
                  className="text-2xl mt-1 group-hover:text-[#f5b14e] transition-colors"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {r.title}
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] mono text-[#f2ece1]/50">
                  <span>{r.category} · {r.year}</span>
                  <span className="inline-flex items-center gap-1 group-hover:text-[#f5b14e]">
                    View <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-[11px] mono uppercase tracking-[0.3em] text-[#f5b14e]">
    ▶ {children}
  </div>
);

const MetaItem = ({ icon, label, value }) => (
  <div className="border-b border-[#2a221b] pb-3">
    <div className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest text-[#f2ece1]/55">
      {icon}
      {label}
    </div>
    <div className="mt-1 text-[16px]">{value}</div>
  </div>
);

export default TapeDetailPage;
